import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Col, Form, Row } from 'antd';
import { MdFileUpload, MdGifBox, MdSend } from 'react-icons/md';
import { BsFillEmojiSmileFill } from 'react-icons/bs';
import { ChatMessageTypeRequest, FileUploadList } from 'models/chatMessage';
import { useSelector } from 'react-redux';
import { selectCurrentUser } from 'redux/userSlice';
import { getLocalStorage } from 'utils/sessionStorage';
import EmojiPicker, { EmojiClickData, EmojiStyle } from 'emoji-picker-react';
import TextArea from 'antd/lib/input/TextArea';
import { useAppSelector, useAppTranslation, useErrTranslation } from 'app/hooks';
import CloseIcon from 'assets/icons/CloseIcon';
import useThemes from 'hooks/useThemes';
import useReplyMessage from 'hooks/useReplyMesssage';
import bet_icon from 'assets/icons/bet-icon.png';
import { IMG_ALT } from 'consts/app';
import { selectShowChatMobile } from 'redux/chatSlice';
import { checkEmptyString, uniqueId } from 'utils/commonUtils';
import { FaXmark } from 'react-icons/fa6';
import { FaFileAlt } from 'react-icons/fa';
import GifPicker, { TenorImage } from 'gif-picker-react';
import chatApi from 'api/chatApi';
import { useHandleResponseError } from 'hooks/useHandleResponseError';
import { showErrorModal } from 'components/Modals/CommonModals';
import { RenderNews } from './RenderMessage';

interface ChatInputProps {
  sendMessage: (message: ChatMessageTypeRequest) => void;
  type?: string;
  isDisable?: boolean;
  handleBlur?: () => void;
  handleCloseReply?: () => void;
  replyOnId?: string;
}

type MessageContent = {
  content: string;
};

const MAXIMUM_FILES_SIZE = 25;

const acceptedExtensions = [
  '.doc',
  '.docx',
  '.xlsx',
  '.xlsm',
  '.xls',
  '.pdf',
  '.png',
  '.jpg',
  '.jpeg',
  '.jtif',
  '.jfif',
  '.gif',
];

const isFileExtensionAccepted = (filename: string) => {
  const extension = filename.slice(filename.lastIndexOf('.')).toLowerCase();
  return acceptedExtensions.includes(extension);
};

const ChatInput: React.FunctionComponent<ChatInputProps> = ({
  sendMessage,
  type,
  isDisable,
  handleBlur,
  handleCloseReply,
  replyOnId = '',
}) => {
  const [form] = Form.useForm();
  const user = useSelector(selectCurrentUser);
  const t = useAppTranslation();
  const et = useErrTranslation();
  const token = getLocalStorage('access_token_terabet');
  const { currentTheme } = useThemes();
  const { clearMessage, replyMessage } = useReplyMessage();
  const inputRef = useRef<HTMLInputElement>(null);
  const selectedShowChatMobile = useAppSelector(selectShowChatMobile);
  const handleResponseError = useHandleResponseError();

  const [isShowEmoji, setIsShowEmoji] = useState(false);
  const [isShowGifPicker, setIsShowGifPicker] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [listFile, setListFile] = useState<FileUploadList[]>([]);
  const [selectedGif, setSelectedGif] = useState<string>('');

  const handleEnter = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey && !isLoading) {
      event.preventDefault();
      setIsLoading(true);
      sendForm(event.currentTarget.value, '');
      sendForm('', selectedGif);
      handleUploadFile();
      form.resetFields();
      setIsLoading(false);
    }
  };

  const onSubmit = async (value: MessageContent) => {
    setIsLoading(true);
    sendForm(value.content, '');
    sendForm('', selectedGif);
    handleUploadFile();
    setIsLoading(false);
  };

  const sendForm = async (message: string, imageUrl: string) => {
    if (user && message !== undefined && (message.trim() !== '' || imageUrl !== '')) {
      const newMessage = {
        message: message,
        type: 'MESSAGE',
        date: new Date(),
        token: token,
        replyOnId: null,
        replyOnMessage: '',
        fileUrl: imageUrl,
      };
      sendMessage(newMessage);
      form.resetFields();
      setSelectedGif('');
      clearMessage();
    }
  };

  const handleUploadFile = async () => {
    if (listFile.length === 0) {
      return;
    }
    const { ok, error } = await chatApi.uploadFile(listFile, replyOnId);
    if (ok) {
      setListFile([]);
      if (handleCloseReply) {
        handleCloseReply();
      }
    }
    if (error) {
      handleResponseError(error);
    }
  };

  const handleShowEmoji = () => {
    setIsShowEmoji((prev) => !prev);
    setIsShowGifPicker(false);
  };

  const handleShowGifPicker = () => {
    setIsShowGifPicker((prev) => !prev);
    setIsShowEmoji(false);
  };

  const handleClickEmoji = (emoji: EmojiClickData, event: MouseEvent) => {
    setIsShowEmoji(false);

    form.setFieldsValue({
      content: form.getFieldValue('content')
        ? form.getFieldValue('content') + emoji.emoji
        : emoji.emoji,
    });
  };

  useEffect(() => {
    if (inputRef.current && replyMessage) {
      inputRef.current.focus();
    }
  }, [replyMessage]);

  const scrollToTop = () => {
    const element = document.getElementById('chat-input-id');
    const elementPositon = element?.clientHeight;
    if (elementPositon)
      setTimeout(() => {
        window.scrollTo({
          top: 0 + elementPositon,
          behavior: 'smooth',
        });
      }, 100);
  };

  const handleFocusText = () => {
    selectedShowChatMobile && scrollToTop();
    setIsShowEmoji(false);
  };

  const onUploadFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const fileList = e.target.files;

    if (fileList && fileList.length > 0) {
      const newFiles = Array.from(fileList).map((file) => ({
        id: uniqueId(),
        content: file,
      }));
      const hasInvalidFile = newFiles.some(({ content }) => !isFileExtensionAccepted(content.name));
      if (hasInvalidFile) {
        showErrorModal({
          title: et('error.upload-file.title'),
          content: et('error.upload-file.invalid-extension'),
          onOk: () => {},
        });
        e.target.value = '';
        return;
      }
      const sizeOfByte =
        listFile.reduce((acc, file) => acc + file.content.size, 0) +
        newFiles.reduce((acc, file) => acc + file.content.size, 0);
      if (sizeOfByte / (1024 * 1024) > MAXIMUM_FILES_SIZE) {
        showErrorModal({
          title: et('error.upload-file.title'),
          content: et('error.upload-file.size-msg'),
          onOk: () => {},
        });
      } else {
        setListFile((prevList) => [...prevList, ...newFiles]);
      }

      e.target.value = '';
    }
  };

  const handleDeleteFile = (fileId: string) => {
    setListFile((prevList) => [...prevList.filter((prev) => prev.id !== fileId)]);
  };

  const handleGifClick = (value: TenorImage) => {
    setSelectedGif(value.url);
    setIsShowGifPicker(false);
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    if (e.clipboardData.files.length) {
      const fileObject = e.clipboardData.files[0];
      if (fileObject.type === 'image/png') {
        const newFiles = {
          id: uniqueId(),
          content: fileObject,
        };
        setListFile((prevList) => [...prevList, newFiles]);
      }
    }
  };

  const reply = useMemo(() => {
    if (replyMessage) {
      switch (replyMessage.messageType) {
        case 'SHARE_BET':
          return (
            <>
              {t('sharebet.follow-btn')}
              <img src={bet_icon} alt={IMG_ALT} className="w-5 h-5 ml-3" />
            </>
          );
        case 'IMAGE':
          return <p>{t('reply-chat.image')}</p>;
        case 'FILE':
          return <p>{t('reply-chat.file')}</p>;
        case 'NEWS':
          const parsedArray = JSON.parse(replyMessage.message);
          try {
            const msg = JSON.parse(parsedArray);
            return <RenderNews news={msg} />;
          } catch (error) {
            return null;
          }
        case 'GIVE_AWAY':
          return <p>{t('chat.give-away.notice')}</p>;
        default:
          return <p>{replyMessage.message}</p>;
      }
    }
    return '';
  }, [replyMessage?.messageId]);

  return (
    <div className="chat-room__chatbox-send">
      <Form name="chat" form={form} onFinish={onSubmit}>
        {replyMessage && (
          <Row gutter={[8, 8]}>
            <Col span={24} className="">
              <div className="reply-message">
                <div className="reply-message-title">
                  <span>
                    {replyMessage.userId === user?.id
                      ? t('reply-message.reply-yourself')
                      : t('reply-message.reply-to-other', {
                          name: checkEmptyString(replyMessage.username)
                            ? t('anonymous')
                            : replyMessage.username,
                        })}
                  </span>
                  <span onClick={() => clearMessage()}>
                    <CloseIcon width="10" height="10" isDarkMode={currentTheme === 'dark'} />
                  </span>
                </div>
                <p className="reply__message">{reply}</p>
              </div>
            </Col>
          </Row>
        )}
        {(listFile.length > 0 || selectedGif !== '') && (
          <Row gutter={[8, 8]}>
            <Col span={24}>
              <div className="reply-message list-upload-file">
                {selectedGif !== '' && (
                  <div className="file-item">
                    <img src={selectedGif} alt="TERABET_GIF" className="uploaded-image" />
                    <span className="delete-file-icon" onClick={() => setSelectedGif('')}>
                      <FaXmark />
                    </span>
                  </div>
                )}
                {listFile.map((item) => {
                  return item.content.type.includes('image/') ? (
                    <div className="file-item">
                      <img
                        src={URL.createObjectURL(item.content)}
                        alt={item.content.name}
                        className="uploaded-image"
                      />
                      <span className="delete-file-icon" onClick={() => handleDeleteFile(item.id)}>
                        <FaXmark />
                      </span>
                    </div>
                  ) : (
                    <div className="file-item file-type">
                      <FaFileAlt />
                      <span className="file-name">{item.content.name}</span>
                      <span className="delete-file-icon" onClick={() => handleDeleteFile(item.id)}>
                        <FaXmark />
                      </span>
                    </div>
                  );
                })}
              </div>
            </Col>
          </Row>
        )}
        <Row gutter={[8, 8]} className="chat-room__chatbox-form">
          <Col span={22} className="chat-room__input-group">
            <Form.Item name="content" style={{ width: '100%', margin: '0' }}>
              <TextArea
                placeholder={t('placeholder.inputMessage')}
                autoSize={{ minRows: 1, maxRows: 1 }}
                className="chat-room__chatbox-input"
                onKeyUp={handleEnter}
                ref={inputRef}
                onFocus={handleFocusText}
                id={'chat-input-id'}
                onPaste={handlePaste}
                onBlur={() => handleBlur && handleBlur()}
              />
            </Form.Item>
            {type !== 'ASSISTANT' && (
              <div className="chat-additional">
                <input
                  type="file"
                  name="file"
                  id="file"
                  className="inputfile"
                  multiple
                  onChange={onUploadFile}
                  accept=".doc,.docx,.xlsx,.xlsm,.xls,.pdf,.png,.jpg,.jpeg,.jtif,.jfif,.gif"
                />
                {handleCloseReply && (
                  <button type="button" className="chat-room__btn icon" onClick={handleCloseReply}>
                    <CloseIcon width="15" height="15" isDarkMode={currentTheme === 'dark'} />
                  </button>
                )}
                <label htmlFor="file" className="chat-room__btn icon">
                  <MdFileUpload />
                </label>
                <button type="button" className="chat-room__btn icon" onClick={handleShowEmoji}>
                  <BsFillEmojiSmileFill />
                </button>
                <button
                  type="button"
                  className="chat-room__btn icon fz-20"
                  onClick={handleShowGifPicker}
                >
                  <MdGifBox />
                </button>
              </div>
            )}
          </Col>
          {isShowEmoji && (
            <div className="chat-room__emoji">
              <EmojiPicker
                width={200}
                height={300}
                searchDisabled={true}
                skinTonesDisabled={true}
                previewConfig={{ showPreview: false }}
                onEmojiClick={handleClickEmoji}
                emojiStyle={EmojiStyle.GOOGLE}
              />
            </div>
          )}
          {isShowGifPicker && (
            <div className="chat-room__emoji">
              <GifPicker
                tenorApiKey={process.env.REACT_APP_GOOGLE_TENOR as string}
                width={200}
                height={300}
                onGifClick={handleGifClick}
              />
            </div>
          )}
          <Col span={2}>
            <button
              type="submit"
              className="chat-room__btn"
              disabled={(type === 'ASSISTANT' && isDisable) || isLoading}
            >
              <MdSend />
            </button>
          </Col>
        </Row>
      </Form>
    </div>
  );
};

export default ChatInput;
